import { QueryFunctionContext, QueryKey, useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getProcedureIds, getProcedures } from 'api/procedures';
import { map, range, reject, uniq } from 'es-toolkit/compat';
import { ProcedureResponse } from 'interfaces/procedure';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { getOneIndexedPageWithOffset } from './api';
import { useEncodedFilters } from './useEncodedFilters';
import { ExperimentResultsSelection } from './useEncodedFilters/types';

interface Props
  extends Omit<UseQueryOptions<any, unknown, ProcedureResponse, QueryKey>, 'initialData' | 'queryKey' | 'queryFn'> {
  forcePage?: number;
  numSurroundingPagesToPrefetch?: number;
  loadIds?: boolean;
  allowCaseReviewJobFilters?: boolean;
}

const caseReviewFilterParams = ['datasetJobFilters', 'jobsFilters'] as const;

export const removeCaseReviewJobFilters = (encodedParams: string): string => {
  const searchParams = new URLSearchParams(encodedParams);
  let removedCaseReviewFilters = false;

  caseReviewFilterParams.forEach((param) => {
    const encodedFilters = searchParams.get(param);
    if (!encodedFilters) {
      return;
    }

    try {
      const filters = JSON.parse(encodedFilters);
      if (!filters?.attemptSelection) {
        return;
      }
    } catch {
      return;
    }

    searchParams.delete(param);
    searchParams.delete(
      param === 'datasetJobFilters' ? 'datasetJobFiltersIncludeExclude' : 'jobsFiltersIncludeExclude'
    );
    removedCaseReviewFilters = true;
  });

  return removedCaseReviewFilters ? searchParams.toString() : encodedParams;
};

export const useProceduresWithQAExperimentResultsOnly = ({
  numSurroundingPagesToPrefetch = 0,
  enabled = true,
  loadIds,
  forcePage,
  allowCaseReviewJobFilters = false,
  ...props
}: Props = {}) => {
  const { encodedFilters, generateEncodedParams, queryParams } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const getProcedureQueryParams = (params: string) =>
    allowCaseReviewJobFilters ? params : removeCaseReviewJobFilters(params);
  const currentPageEncodedParams = getProcedureQueryParams(
    !isNaN(forcePage) ? generateEncodedParams({ page: forcePage }) : encodedFilters
  );
  const queryResults = useQuery<any, unknown, ProcedureResponse>({
    ...props,
    enabled,
    queryKey: ['procedures', currentPageEncodedParams],
    queryFn: ({ signal }: QueryFunctionContext) => {
      return getProcedures(currentPageEncodedParams, signal);
    },
  });

  const currentPage = (forcePage || queryParams.page || 1) as number;
  const pageSize = queryParams.pageSize || DEFAULT_PAGE_SIZE;
  const pageCount = queryParams?.slidesMode
    ? Math.ceil((queryResults?.data?.totalSlides ?? 0) / pageSize)
    : Math.ceil((queryResults?.data?.totalProcedures ?? 0) / pageSize);

  const additionalPagesToFetch =
    (pageCount ?? 0) > 1
      ? reject(
          uniq(
            map(range(-1 * numSurroundingPagesToPrefetch, numSurroundingPagesToPrefetch + 1), (i) =>
              getOneIndexedPageWithOffset(currentPage, i, pageCount)
            )
          ),
          (page) => page === currentPage
        )
      : [];

  useQueries({
    queries: map(additionalPagesToFetch, (page) => ({
      ...props,
      enabled,
      queryKey: ['procedures', getProcedureQueryParams(generateEncodedParams({ page }))],
      queryFn: ({ signal }: QueryFunctionContext) => {
        return getProcedures(getProcedureQueryParams(generateEncodedParams({ page })), signal);
      },
    })),
  });

  const procedureIdsEncodedParams = getProcedureQueryParams(encodedFilters);
  useQuery(['procedureIds', [procedureIdsEncodedParams]], {
    queryFn: () => getProcedureIds(procedureIdsEncodedParams),
    enabled: Boolean(queryResults.data) && loadIds,
  });

  return queryResults;
};
